import { FiEye } from 'react-icons/fi';

export const identifyingWriterViewsData = {
  courseId: 'identifying-writer-views',
  courseType: 'reading-course',
  title: 'Identifying Writer Views',
  icon: FiEye,
  overview: {
    impact: 'Separate fact, reported opinion, and author stance more accurately',
    focus: 'Evaluative language, tone, and source attribution',
    uses: 'Advanced reading comprehension and inference',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'Where Writer Views Appear',
      content:
        '<p>Writer views are often signaled through <strong>evaluation</strong>, <strong>certainty</strong>, and <strong>contrast</strong>. The author may approve, criticize, doubt, or remain cautious.</p>',
      points: [
        'Words like unfortunately, surprisingly, crucial, or misleading often reveal attitude.',
        'Strong certainty words such as clearly or undoubtedly show firmer stance than may or perhaps.',
        'A writer can sound neutral at first and reveal a view later through contrast words such as however or yet.',
      ],
    },
    {
      title: 'Writer vs Other Voices',
      content:
        '<p>One of the hardest problems is deciding whose opinion you are reading.</p>',
      points: [
        'A quoted expert opinion is not automatically the writer\'s opinion.',
        'If the passage says "critics argue..." or "some researchers believe...", that stance may belong to someone else.',
        'Look for the sentence that follows the quotation. The writer often confirms, questions, or limits it there.',
      ],
      warning:
        'Do not confuse reported speech with author endorsement.',
    },
    {
      title: 'A Quick Reading Routine',
      content: '<p>To identify the writer\'s view efficiently:</p>',
      points: [
        'Mark any adjective or adverb that carries judgement.',
        'Notice who is speaking in each sentence.',
        'Track contrast markers because the author\'s true view often appears after them.',
        'Ask whether the author is describing, approving, doubting, or recommending.',
      ],
    },
    {
      title: 'Common Signals of Stance',
      content: '<p>These patterns often reveal the writer\'s attitude:</p>',
      table: {
        headers: ['Signal', 'Likely Effect'],
        rows: [
          ['remarkably / encouragingly', 'positive evaluation'],
          ['unfortunately / problematic', 'negative evaluation'],
          ['may / appears to / suggests', 'cautious or limited claim'],
          ['must / essential / should', 'recommendation or strong position'],
          ['however / yet / nevertheless', 'shift toward the real point'],
        ],
      },
    },
  ],
  exercises: [
    {
      title: 'Whose View Is It?',
      type: 'select',
      instructions: 'Choose the best description of the viewpoint.',
      questions: [
        {
          text:
            'Passage: "Several business leaders praised the proposal, but the writer notes that the evidence for long-term savings remains weak."\nWhat is the writer\'s position?',
          options: [
            'The writer fully supports the proposal.',
            'The writer is cautious about the proposal\'s claims.',
            'The writer praises business leaders.',
          ],
          answer: 'The writer is cautious about the proposal\'s claims.',
          explanation:
            'The phrase "evidence ... remains weak" shows caution and skepticism.',
        },
        {
          text:
            'Passage: "Critics describe the app as intrusive. The article, however, argues that its privacy settings are stronger than those of its competitors."\nWhat is the writer\'s position?',
          options: [
            'The writer agrees completely with the critics.',
            'The writer defends the app against the criticism.',
            'The writer avoids taking any position.',
          ],
          answer: 'The writer defends the app against the criticism.',
          explanation:
            'The contrast marker "however" introduces the writer\'s counter-position.',
        },
      ],
    },
    {
      title: 'Identify the Attitude Signal',
      type: 'select',
      instructions: 'Choose the phrase that best signals the writer\'s view.',
      questions: [
        {
          text:
            'Which phrase most clearly signals a negative author attitude?',
          options: ['problematic assumption', 'data table', 'previous section'],
          answer: 'problematic assumption',
          explanation:
            '"Problematic" is evaluative and critical.',
        },
        {
          text:
            'Which phrase most clearly signals caution rather than certainty?',
          options: ['appears to suggest', 'clearly proves', 'without any doubt'],
          answer: 'appears to suggest',
          explanation:
            'This wording deliberately lowers certainty.',
        },
      ],
    },
    {
      title: 'Type the Best Description',
      type: 'fill-in',
      instructions: 'Type one word: positive, negative, or cautious.',
      questions: [
        {
          text:
            'Passage: "Although early outcomes are promising, it would be premature to call the program a success."\nWriter attitude:',
          answer: 'cautious',
          explanation:
            'The writer acknowledges promise but avoids a strong positive conclusion.',
        },
        {
          text:
            'Passage: "The report offers a remarkably clear account of the failures behind the policy."\nWriter attitude:',
          answer: 'negative',
          explanation:
            'Despite praise for the clarity, the focus of the attitude toward the policy is negative.',
        },
        {
          text:
            'Passage: "The redesigned station has significantly improved access for elderly passengers."\nWriter attitude:',
          answer: 'positive',
          explanation:
            '"Improved access" signals approval of the redesign.',
        },
      ],
    },
    {
      title: 'Explain the Writer\'s Stance',
      type: 'writing',
      instructions: 'Explain which words reveal the writer\'s view.',
      questions: [
        {
          text:
            'Passage: "The initiative was widely praised at launch, yet the author argues that its benefits were overstated."\nHow do you know the writer is skeptical?',
          example:
            'The writer is skeptical because "yet" introduces a contrast and the phrase "benefits were overstated" shows the author thinks the praise was exaggerated.',
          tip:
            'Quote the exact evaluative phrase if possible.',
        },
      ],
    },
  ],
  tips: [
    'Track who owns each opinion before answering.',
    'Contrast words often reveal the author\'s true position.',
    'Evaluative adjectives are stronger clues than topic nouns.',
    'Cautious language usually beats extreme interpretation in IELTS.',
  ],
  dailyPractice: [
    'Underline all evaluative words in one opinion article.',
    'Label each paragraph as descriptive, critical, supportive, or cautious.',
    'Take one quoted opinion and decide whether the writer endorses it or not.',
    'Rewrite one neutral sentence into a positive and a negative version to feel tone shifts.',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext:
    'Identifying Writer Views: recognizing author stance, separating writer opinion from reported views, and tracking evaluative language and tone.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...identifyingWriterViewsData,
      title: identifyingWriterViewsData.title + ' (Foundations)',
      overview: { ...identifyingWriterViewsData.overview, duration: '15-20 minutes' },
      sections: identifyingWriterViewsData.sections.slice(0, 2),
      exercises: [
        {
          ...identifyingWriterViewsData.exercises[0],
          title: 'Whose View Is It? (Basic)',
          questions: identifyingWriterViewsData.exercises[0].questions.slice(0, 1),
        },
        {
          ...identifyingWriterViewsData.exercises[2],
          title: 'Type the Best Description (Basic)',
          questions: identifyingWriterViewsData.exercises[2].questions.slice(0, 2),
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...identifyingWriterViewsData,
      title: identifyingWriterViewsData.title + ' (Advanced)',
      overview: { ...identifyingWriterViewsData.overview, duration: '30-35 minutes' },
      sections: [
        ...identifyingWriterViewsData.sections,
        {
          title: 'Advanced Tone Shifts',
          content:
            '<p>At higher levels, the writer may sound balanced before taking a subtle position. Pay attention to late-stage qualifiers and contrast clauses.</p>',
          points: [
            'A sentence can begin neutrally and end critically.',
            'The most important view sometimes appears in the final clause.',
            'Mild wording can still signal disagreement if it narrows the claim.',
          ],
        },
      ],
      exercises: [
        ...identifyingWriterViewsData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        {
          title: 'Advanced Tone Challenge',
          type: 'select',
          instructions: 'Choose the most accurate reading of the writer\'s attitude.',
          questions: [
            {
              text:
                'Passage: "The proposal is imaginative, but its practical benefits remain uncertain."\nWriter attitude:',
              options: [
                'fully supportive',
                'admiring but unconvinced',
                'strongly hostile',
              ],
              answer: 'admiring but unconvinced',
              explanation:
                'The writer acknowledges creativity while withholding full support.',
            },
          ],
        },
      ],
      tips: [
        ...(identifyingWriterViewsData.tips || []),
        'At Band 8+, distinguish polite skepticism from open criticism.',
        'If a writer hedges a claim, do not upgrade it into a strong opinion.',
      ],
    };
  }
  return identifyingWriterViewsData;
}
